import type { z } from 'zod';
import type {
  WebhookEndpointSchema,
  WebhookEndpointWithSecretSchema,
  WebhookDeliverySchema,
} from '@contract/webhooks';
import { apiClient } from './api';

/**
 * Webhook endpoint management (REV-442).
 *
 * Mirrors the API's DTOs rather than the database rows: the server never
 * returns a signing secret except the once-only plaintext at creation and
 * rotation, so `secret` appears on those two result types and nowhere else.
 */

/**
 * REV-821: the response types come from the vendored contract, which
 * revenant-api checks against its real routes. The hand-typed versions had
 * drifted: the create response has no `secretRotatedAt`, and a failed test
 * send omitted `deliveredAt` altogether (fixed API-side to send null).
 */
export type WebhookEndpoint = z.infer<typeof WebhookEndpointSchema>;
/**
 * Creation and rotation are the ONLY responses that carry a plaintext secret,
 * and it cannot be retrieved afterwards. The UI must show it once, plainly.
 */
export type WebhookEndpointWithSecret = z.infer<typeof WebhookEndpointWithSecretSchema>;
export type WebhookDelivery = z.infer<typeof WebhookDeliverySchema>;
export type WebhookStatus = WebhookEndpoint['status'];
export type DeliveryStatus = WebhookDelivery['status'];

export interface CreateWebhookData {
  name: string;
  url: string;
  eventTypes: string[];
  contextId?: string | null;
  status?: WebhookStatus;
}

export type UpdateWebhookData = Partial<CreateWebhookData>;

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  requestId?: string;
}

interface DeliveryListResponse extends ApiResponse<WebhookDelivery[]> {
  total: number;
}

/**
 * The event types an endpoint can subscribe to, with human labels.
 *
 * Kept here rather than fetched because the vocabulary is a compile-time
 * contract with the API's `eventTypes.js` — a type the server does not know is
 * rejected at the boundary, so discovering the list at runtime would only move
 * the failure later.
 */
export const SUBSCRIBABLE_EVENTS = [
  { value: 'events.scan.completed', labelKey: 'webhooks.events.scanCompleted' },
  { value: 'events.scan.failed', labelKey: 'webhooks.events.scanFailed' },
  { value: 'events.finding.created', labelKey: 'webhooks.events.findingCreated' },
  { value: 'events.finding.updated', labelKey: 'webhooks.events.findingUpdated' },
  {
    value: 'events.conformance_snapshot.created',
    labelKey: 'webhooks.events.snapshotCreated',
  },
  { value: 'events.audit_session.finalized', labelKey: 'webhooks.events.auditFinalized' },
  { value: 'events.audit_session.locked', labelKey: 'webhooks.events.auditLocked' },
  { value: 'events.context.created', labelKey: 'webhooks.events.contextCreated' },
  {
    value: 'events.conformance.regression_detected',
    labelKey: 'webhooks.events.regressionDetected',
  },
] as const;

export const webhookService = {
  list: async (orgId: string, contextId?: string) => {
    const response = await apiClient.get<ApiResponse<WebhookEndpoint[]>>(
      `/v1/organizations/${orgId}/webhooks`,
      // apiClient.get takes the params object DIRECTLY, not wrapped in
      // `{ params }` — wrapping it double-nests and the query silently
      // disappears (REV-530).
      contextId ? { contextId } : undefined
    );
    return response.data;
  },

  getById: async (orgId: string, webhookId: string) => {
    const response = await apiClient.get<ApiResponse<WebhookEndpoint>>(
      `/v1/organizations/${orgId}/webhooks/${webhookId}`
    );
    return response.data;
  },

  create: async (orgId: string, data: CreateWebhookData) => {
    const response = await apiClient.post<ApiResponse<WebhookEndpointWithSecret>>(
      `/v1/organizations/${orgId}/webhooks`,
      data
    );
    return response.data;
  },

  update: async (orgId: string, webhookId: string, data: UpdateWebhookData) => {
    const response = await apiClient.patch<ApiResponse<WebhookEndpoint>>(
      `/v1/organizations/${orgId}/webhooks/${webhookId}`,
      data
    );
    return response.data;
  },

  remove: async (orgId: string, webhookId: string) => {
    const response = await apiClient.delete<ApiResponse<{ id: string; deleted: boolean }>>(
      `/v1/organizations/${orgId}/webhooks/${webhookId}`
    );
    return response.data;
  },

  rotateSecret: async (orgId: string, webhookId: string) => {
    const response = await apiClient.post<ApiResponse<WebhookEndpointWithSecret>>(
      `/v1/organizations/${orgId}/webhooks/${webhookId}/rotate-secret`,
      {}
    );
    return response.data;
  },

  sendTest: async (orgId: string, webhookId: string) => {
    const response = await apiClient.post<ApiResponse<WebhookDelivery>>(
      `/v1/organizations/${orgId}/webhooks/${webhookId}/test`,
      {}
    );
    return response;
  },

  listDeliveries: async (
    orgId: string,
    webhookId: string,
    params?: { limit?: number; offset?: number; status?: DeliveryStatus }
  ) => {
    const response = await apiClient.get<DeliveryListResponse>(
      `/v1/organizations/${orgId}/webhooks/${webhookId}/deliveries`,
      // Params passed directly; see the note in list() (REV-530).
      params as Record<string, unknown> | undefined
    );
    return response;
  },
};
